/* eslint-disable no-underscore-dangle */
import moment from 'moment';
import { isNull, each, orderBy, isNil, filter, includes, remove, get, cloneDeep, map, keys, union, find, isUndefined, omit, pickBy, isEmpty } from 'lodash';
import { i18n } from 'vue-infra';
import {
  DATA_LOADING_STATES,
  WIDGET_LOGICAL_NAMES,
  PARTIAL_WIDGET_UNSUPPORTED_METRICS,
  USER_TYPES,
  CHART_THEMES_PARAMS,
  GRAPH_RESOLUTION,
  FEATURES,
  METRIC_NAMES,
  SORT_DIRECTION,
  WIDGET_MODES,

} from '@config/Consts';
import { ALERT_CONFIG } from '@config/AlertConfig';
import { ROOT_GETTER_PATHS, INFRA_GETTERS } from '@core/store';
import { getLastTimeUpdated, getMetricLabel, getDialogAlertItemsByWidget } from '@core/utils/utils';
import botsGraphTemplate from './botsGraphTemplate';
import { RESOURCES_METRICS_MAP, BOT_LIST_RESOURCES } from './Consts';


// eslint-disable-next-line max-len
const _getAlertDataByMetricName = (state, getters, metricName) => pickBy(getters.getAlertData, (value, key) => includes([metricName], key));

const _getDialogAlertItemsByMetricName = (state, getters, metricName) =>
  getDialogAlertItemsByWidget([metricName], WIDGET_LOGICAL_NAMES.BOTS, getters.getAlertData);

const resourceMetricsUnsupported = {};

const _getDataStatus = state => state.dataStatus;

const _getBotListDataStatus = (state) => {
  const result = omit(state.dataStatus, [BOT_LIST_RESOURCES.HISTORICAL]);
  return result;
};

const _getLastTimeDataUpdated = state => state.lastTimeDataUpdated;
// eslint-disable-next-line no-unused-vars
const getMetricScoreByMetricName = state => (metricName) => {
  let metricScore;
  switch (metricName) {
    case METRIC_NAMES.AGENT_VIEW_PENDING_RATE:
      // todo get pending rate value
      metricScore = 0;
      break;
    default:
      metricScore = null;
      break;
  }
  return metricScore;
};


const getMetricAttributes = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_WIDGET_METRIC_ATTRIBUTES](WIDGET_LOGICAL_NAMES.BOTS);

const getTimeFrameFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_TIMEFRAME_FILTER];

const getUserIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_USERS];

const getUsers = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_USERS];

const getBotsInGroup = (state, getters) => {
  const accountUsers = getters.getUsers;
  const usersInGroupIds = getters.getUserIds;
  return filter(accountUsers,
    user => user.userType === USER_TYPES.BOT && includes(usersInGroupIds, user.id));
};
const getAlertData = (state, getters, rootState, rootGetters) => {
  const allAlerts = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ALERT_DATA];
  return get(allAlerts, WIDGET_LOGICAL_NAMES.BOTS) || {};
};
const getStatusReasons = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_STATUS_REASONS];

const getFilteredGroupsIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GROUPS_FILTER];

const getSkillsFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_SKILLS_IDS];

const getBotsGraphUserSettings = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_BOTS_GRAPH_USER_SETTINGS];

const getShowGraphFromManifest = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_SHOW_BOT_GRAPH];

const getBotsMetrics = state => state.botsMetrics || [];


const getDialogAlertItemsByMetricName = (state, getters) => (metricName) => {
  let alertItems;
  switch (metricName) {
    case METRIC_NAMES.AGENT_VIEW_PENDING_RATE:
      alertItems =
        _getDialogAlertItemsByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_PENDING_RATE);
      break;
    default:
      alertItems = [];
      break;
  }
  return alertItems;
};

const showAlertConfigButtonForPendingRate = (state, getters, rootState, rootGetters) => {
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];
  const showAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.BOTS}.${METRIC_NAMES.AGENT_VIEW_PENDING_RATE}.showAlertConfiguration`, false);
  return showAlertConfig && alertPermissions.canSetAlert;
};
const isAlertConfiguredForPendingRate = (state, getters) => {
  const alerts = _getAlertDataByMetricName(state, getters, METRIC_NAMES.AGENT_VIEW_PENDING_RATE);
  return !isEmpty(alerts);
};

const getWidgetMode = state => state.widgetMode;

const getStatusFilter = state => state.filter.status;

const getBotsFilter = state => state.filter.bots;

const getTotalBotStatuses = state => state.botStatusSummary;

const getSort = state => state.sort;

const getCurrentPageBotIds = state => state.currentPageBotIds;

const getGlobalFiltersUpdating = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GLOBAL_FILTERS_UPDATING][WIDGET_LOGICAL_NAMES.BOTS];

const dataHasBeenFetched = state => !isNull(state.botsMetrics) && state.botsMetrics.length > 0;

const graphDataHasBeenFetched = state => !isNull(state.graphMetrics);

const _useWorkspacePartialWidgets = rootGetters =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_USE_PARTIAL_WIDGETS];

const _isAllResourceMetricsUnsupported = (rootGetters, resource) => {
  const unsupportedMetrics = get(PARTIAL_WIDGET_UNSUPPORTED_METRICS, WIDGET_LOGICAL_NAMES.BOTS);
  if (isNil(resourceMetricsUnsupported[resource])) {
    let result = false;
    if (_useWorkspacePartialWidgets(rootGetters)) {
      const resourceMetrics = get(RESOURCES_METRICS_MAP, resource);
      if (resourceMetrics) {
        let allResourceMetricsUnsupported = true;
        each(resourceMetrics, (metric) => {
          allResourceMetricsUnsupported =
            allResourceMetricsUnsupported &&
            includes(unsupportedMetrics, metric);
        });
        result = allResourceMetricsUnsupported;
      }
    }
    resourceMetricsUnsupported[resource] = result;
  }

  return resourceMetricsUnsupported[resource];
};

const _isResourceOutOfDate = (state, resourceName) =>
  _getDataStatus(state)[resourceName] === DATA_LOADING_STATES.ERROR ||
  _getDataStatus(state)[resourceName] === DATA_LOADING_STATES.OBSOLETE;

const _getOutOfDateResources = (state) => {
  const obsoleteResources = [];
  each(Object.keys(_getBotListDataStatus(state)), (resource) => {
    if (_isResourceOutOfDate(state, resource)) {
      obsoleteResources.push(resource);
    }
  });
  return obsoleteResources;
};

const _isOneOfTheResourceInSpecificStatus = ({ state, rootGetters }, status) => {
  let isOneOfTheResourceInSpecificStatus = false;
  each(Object.keys(_getBotListDataStatus(state)), (resource) => {
    const allResourceMetricsUnsupported =
      _isAllResourceMetricsUnsupported(rootGetters, resource);
    isOneOfTheResourceInSpecificStatus =
      isOneOfTheResourceInSpecificStatus ||
      (!allResourceMetricsUnsupported && _getDataStatus(state)[resource] === status);
  });
  return isOneOfTheResourceInSpecificStatus;
};

const _isOneOfTheResourcesDown = (state, rootGetters) =>
  _isOneOfTheResourceInSpecificStatus({ state, rootGetters }, DATA_LOADING_STATES.ERROR);

const _isOneOfTheResourcesObsolete = (state, rootGetters) =>
  _isOneOfTheResourceInSpecificStatus({ state, rootGetters }, DATA_LOADING_STATES.OBSOLETE);

const _getMetricNamesFromGraphData = (data) => {
  let names = [];
  map(data, (item) => {
    names = union(names, keys(item.values));
  });
  return names;
};

const _formatGraphValues = (data, metricNames, timestamps, graphThemeSettings) => {
  const result = [];
  each(metricNames, (name, ind) => {
    const metricData = [];
    each(timestamps, (ts) => {
      const item = find(data, d => d.timestamp === ts);
      const val = get(item, `values.${name}`);
      if (!isUndefined(val)) {
        metricData.push([ts, val]);
      }
    });
    const metricColors = graphThemeSettings[CHART_THEMES_PARAMS.METRIC_COLORS];
    const customColorExist = ind < metricColors.length;
    const graphMetricItem = {
      name: i18n.t(`amd:bots_graph_legend_${name}`),
      type: 'line',
      smooth: true,
      showSymbol: timestamps.length <= 1,
      symbol: 'circle',
      lineStyle: {
        width: 1.5,
      },
      symbolSize: timestamps.length > 1 ? 0 : 10,
      data: metricData,
      areaStyle: {},
    };
    if (customColorExist) {
      graphMetricItem.color = metricColors[ind].color;
      graphMetricItem.areaStyle.color = metricColors[ind].areaColor;
    }
    result.push(graphMetricItem);
  });
  return result;
};

const isWaitingForData = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.LOADING
  || _getDataStatus(state)[BOT_LIST_RESOURCES.AGENT_VIEW] === DATA_LOADING_STATES.LOADING;

// In this widget we want to have an error just in case the agent view data has already been fetched
// because otherwise we have a full widget error instead
const isDataObsolete = (state, getters, rootState, rootGetters) =>
  (_isOneOfTheResourcesObsolete(state, rootGetters) ||
    _isOneOfTheResourcesDown(state, rootGetters))
  && dataHasBeenFetched(state);

const isGraphDataObsolete = (state) => {
  const status = _getDataStatus(state)[BOT_LIST_RESOURCES.HISTORICAL];
  const isStatusObsolete = status === DATA_LOADING_STATES.OBSOLETE;
  const isStatusError = status === DATA_LOADING_STATES.ERROR;
  const graphDataFetched = graphDataHasBeenFetched(state);
  return (isStatusObsolete || isStatusError) && graphDataFetched;
};
const isBotStatusApiOutOfDate = state =>
  _isResourceOutOfDate(state, BOT_LIST_RESOURCES.METRICS);

const botsApiIsDown = (state, getters, rootState, rootGetters) =>
  (rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.ERROR
    || _getDataStatus(state)[BOT_LIST_RESOURCES.AGENT_VIEW] === DATA_LOADING_STATES.ERROR)
  && !dataHasBeenFetched(state);

const graphApiIsDown = (state, getters, rootState, rootGetters) =>
  (rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS]
    === DATA_LOADING_STATES.ERROR
    || _getDataStatus(state)[BOT_LIST_RESOURCES.HISTORICAL] === DATA_LOADING_STATES.ERROR)
  && !graphDataHasBeenFetched(state);

const outOfDateMetrics = (state, getters, rootState, rootGetters) => {
  const resultMetrics = [];
  each(Object.keys(state.dataStatus), (resource) => {
    if (!_isAllResourceMetricsUnsupported(rootGetters, resource) &&
      _isResourceOutOfDate(state, resource)) {
      each(RESOURCES_METRICS_MAP[resource], (metricName) => {
        const metricLabel = getMetricLabel(WIDGET_LOGICAL_NAMES.BOTS, metricName);
        if (!isNil(metricLabel)) {
          resultMetrics.push(metricLabel);
        }
      });
    }
  });
  return resultMetrics;
};

// As we have a different error message for Agent Status API
// we want to get the last time it was updated directly
const botStatusApiLastTimeUpdated = (state) => {
  const lastTimeApiUpdated = _getLastTimeDataUpdated(state)[BOT_LIST_RESOURCES.METRICS];
  return lastTimeApiUpdated ? getLastTimeUpdated(lastTimeApiUpdated) : null;
};

// In case we have couple of APIs failures
// we want to get the earliest time
const lastTimeDataUpdated = (state) => {
  const obsoleteResources = _getOutOfDateResources(state);
  remove(obsoleteResources, resource => resource === BOT_LIST_RESOURCES.METRICS);
  const obsoleteResourcesTimeUpdated =
    filter(_getLastTimeDataUpdated(state), (value, key) =>
      !isNil(value) && includes(obsoleteResources, key));
  const orderedDataUpdateTimes = orderBy(obsoleteResourcesTimeUpdated, ['asc']);
  const earliestTimeDataWasUpdated = orderedDataUpdateTimes ? orderedDataUpdateTimes[0] : null;
  return earliestTimeDataWasUpdated ? getLastTimeUpdated(earliestTimeDataWasUpdated) : null;
};

const graphIsActivated = state => state.graphActivated;

const getGraphFocusBotId = state => state.graphFocusBotId;

const getGraphFocusBotName = (state, getters) => {
  const focusBotId = getGraphFocusBotId(state);
  if (isNull(focusBotId)) {
    return undefined;
  }
  const bot = find(getters.getUsers, user => user.id === Number(focusBotId));
  return bot ? bot.name : undefined;
};

const _generateGraphTooltip = (params) => {
  let result = '';
  if (params.length > 0) {
    const dataIndex = params[0].dataIndex;
    const isLastPoint = dataIndex === (GRAPH_RESOLUTION - 1);
    const strNow = i18n.t('amd:bots_graph_now');
    result = isLastPoint ? `${strNow}<br />` : `${params[0].axisValueLabel.substring(0, 5)}<br />`;
  }
  for (let i = 0; i < params.length; i += 1) {
    result += `${params[i].marker} ${params[i].seriesName}: ${params[i].value[1]}`;
    if (i < (params.length - 1)) {
      result += '<br />';
    }
  }
  return result;
};

const _convertTimestamp = ts => moment(ts).format('HH:mm');

const _generateAriaDescription = (dataSeries) => {
  const mainTitle = i18n.t('amd:bots_graph_aria_main_title');
  const dataPrefix = i18n.t('amd:bots_graph_aria_data_prefix');
  const chartTypeTitle = i18n.t('amd:bots_graph_aria_chartType_title', { seriesCount: dataSeries.length });
  let description = `${mainTitle}${chartTypeTitle}`;
  each(dataSeries, (dataItem) => {
    const subTitle = i18n.t('amd:bots_graph_aria_chartType_subtitle', { seriesName: dataItem.name });
    description += `${subTitle}${dataPrefix}`;
    each(dataItem.data, (d) => {
      const time = _convertTimestamp(d[0]);
      const dataLine = i18n.t('amd:bots_graph_aria_data_dictionary', { name: time, value: d[1] });
      description += `${dataLine}, `;
    });
  });
  return description;
};

const getGraph = state => (graphThemeSettings) => {
  let result = {};
  const graphMetrics = state.graphMetrics;
  if (graphMetrics.length > 0) {
    result = cloneDeep(botsGraphTemplate);
    const metricNames = _getMetricNamesFromGraphData(graphMetrics);
    const timestamps = map(graphMetrics, data => data.timestamp);
    if (get(result, 'xAxis.axisLabel')) {
      result.xAxis.axisLabel.formatter = value => _convertTimestamp(value);
    }
    result.legend.selected = state.graphSelectedLegend;
    result.legend.tooltip.formatter = i18n.t('amd:bots_graph_legend_metric_tooltip');
    result.legend.textStyle.color = graphThemeSettings[CHART_THEMES_PARAMS.LEGEND_TEXT_COLOR];
    result.xAxis.axisLabel.color = graphThemeSettings[CHART_THEMES_PARAMS.AXIS_LABEL_COLOR];
    result.xAxis.axisLine.lineStyle.color =
      graphThemeSettings[CHART_THEMES_PARAMS.AXIS_LINE_COLOR];
    result.yAxis.axisLine.lineStyle.color =
      graphThemeSettings[CHART_THEMES_PARAMS.AXIS_LINE_COLOR];
    result.yAxis.axisLabel.color = graphThemeSettings[CHART_THEMES_PARAMS.AXIS_LABEL_COLOR];
    result.legend.data = map(metricNames, name => i18n.t(`amd:bots_graph_legend_${name}`));
    result.tooltip.formatter = params => _generateGraphTooltip(params);
    const dataSeries =
      _formatGraphValues(graphMetrics, metricNames, timestamps, graphThemeSettings);
    result.series = dataSeries;
    result.aria.description = _generateAriaDescription(dataSeries);
  }
  return result;
};

const botGraphApiLastTimeUpdated = (state) => {
  const lastTimeApiUpdated = _getLastTimeDataUpdated(state)[BOT_LIST_RESOURCES.HISTORICAL];
  return lastTimeApiUpdated ? getLastTimeUpdated(lastTimeApiUpdated) : null;
};

const hasContactCenterFeature = (state, getters, rootState, rootGetters) =>
  rootGetters[INFRA_GETTERS.FEATURE_STATE](FEATURES.CONTACT_CENTER);

const getPresetValues = (state, getters) => ({
  statusFilter: getters.getStatusFilter || [],
  botsFilter: getters.getBotsFilter || [],
  widgetMode: getters.getWidgetMode || WIDGET_MODES.CARDS,
  sort: getters.getSort || {
    columnName: METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS,
    direction: SORT_DIRECTION.DESCENDING,
  },
});

const getAlertPermissions = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];

export default {
  getMetricAttributes,
  getTimeFrameFilter,
  getUserIds,
  getUsers,
  getBotsInGroup,
  getBotsMetrics,
  getStatusReasons,
  getFilteredGroupsIds,
  getWidgetMode,
  getStatusFilter,
  getSkillsFilter,
  getBotsFilter,
  getTotalBotStatuses,
  getSort,
  getCurrentPageBotIds,
  getGlobalFiltersUpdating,
  isDataObsolete,
  dataHasBeenFetched,
  isWaitingForData,
  botsApiIsDown,
  outOfDateMetrics,
  lastTimeDataUpdated,
  isBotStatusApiOutOfDate,
  botStatusApiLastTimeUpdated,
  graphIsActivated,
  getGraphFocusBotId,
  getGraph,
  getGraphFocusBotName,
  graphDataHasBeenFetched,
  graphApiIsDown,
  isGraphDataObsolete,
  botGraphApiLastTimeUpdated,
  getBotsGraphUserSettings,
  hasContactCenterFeature,
  getPresetValues,
  getShowGraphFromManifest,
  showAlertConfigButtonForPendingRate,
  isAlertConfiguredForPendingRate,
  getDialogAlertItemsByMetricName,
  getMetricScoreByMetricName,
  getAlertData,
  getAlertPermissions,
};
